import React, { useContext, useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { Chip, debounce } from "@mui/material";
import MainContext from "../../context/mainContext";
import { useTranslation } from "react-i18next";

interface Props<T> {
  id?: string | undefined;
  placeholder?: string | undefined;
  defaultValue: T[];
  readOnly: boolean;
  error?: string | string[] | false | undefined;
  disabled?: boolean | undefined;
  onChange: (newValue: T[]) => void;
  onBlur: (event: any) => void;
  search: (query: string) => Promise<T[]>;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
}

const MIN_LENGTH = 3;
const DEBOUNCE_MS = 400;

function _genericMultipleSelectPicker<T extends any>({
  id,
  defaultValue,
  placeholder,
  readOnly,
  error,
  disabled,
  onChange,
  onBlur,
  search,
  getOptionLabel,
  isOptionEqualToValue,
}: Props<T>) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<readonly T[]>([]);
  const [value, setValue] = useState<T[] | undefined>([]);
  const [inputValue, setInputValue] = useState("");
  const [searching, setSearching] = useState(false);

  const { service } = useContext(MainContext);

  const handleBlur = (event: any) => {
    if (onBlur) onBlur(event);
  };

  const fetchData = useMemo(
    () =>
      debounce((request: { query: string }, callback: (results?: readonly T[]) => void) => {
        setSearching(true);
        service(() => search(request.query), {
          callback: callback,
          loader: setSearching,
        });
      }, DEBOUNCE_MS),
    [service]
  );

  useEffect(() => {
    let active = true;

    if (inputValue === "" || inputValue.length < MIN_LENGTH) {
      setOptions(value ? [...value] : []);
      return undefined;
    }

    setSearching(true);

    fetchData({ query: inputValue }, (results?: readonly T[]) => {
      if (active) {
        let newOptions: readonly T[] = [];

        if (value) {
          newOptions = [...value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchData]);

  useEffect(() => {
    setValue([...defaultValue]);
  }, [defaultValue]);

  return (
    <Autocomplete
      id={id}
      getOptionLabel={(option) => (typeof option === "string" ? option : getOptionLabel(option))}
      filterOptions={(x) => x}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      placeholder={placeholder}
      autoComplete
      multiple
      includeInputInList
      filterSelectedOptions
      readOnly={readOnly}
      disabled={disabled}
      value={value}
      noOptionsText={inputValue?.length < MIN_LENGTH || searching ? t("enterAtLeast3Characters") : t("noMatches")}
      onBlur={handleBlur}
      onChange={(event: any, newValue: T[] | undefined) => {
        setOptions(newValue ? [...newValue, ...options] : options);
        setValue(newValue);
        onChange(newValue || []);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderTags={(value: readonly T[], getTagProps) =>
        value.map((option: T, index: number) => {
          const optLabel = getOptionLabel(option);
          return <Chip variant="filled" label={optLabel} {...getTagProps({ index })} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={placeholder}
          helperText={error}
          error={Boolean(error)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

const GenericMultipleSelectPicker = React.memo(_genericMultipleSelectPicker) as typeof _genericMultipleSelectPicker;

export default GenericMultipleSelectPicker;
